import React, { useState } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Typography, Box, Divider, 
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, TextField,
  InputAdornment, IconButton, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';

/**
 * Modal component to display dead stock items
 */
const DeadStockList = ({ open, onClose, data, fullScreen = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  if (!data) {
    return null;
  }

  const filteredItems = data.items?.filter(item => 
    item.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.variant_sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  const columns = [
    { field: 'variant_sku', headerName: 'SKU', width: 130 },
    { field: 'product_name', headerName: 'Product', width: 200, flex: 1 },
    { field: 'category', headerName: 'Category', width: 130 },
    { 
      field: 'days_without_sale', 
      headerName: 'Days Without Sale', 
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={`${params.value} days`} 
          color={params.value > 180 ? "error" : "warning"} 
          size="small" 
        />
      )
    },
    { field: 'stock', headerName: 'Stock', width: 100, type: 'number' },
    { 
      field: 'stock_value', 
      headerName: 'Value', 
      width: 130,
      valueFormatter: (params) => {
        if (params === null || params === undefined) {
          return '₹ 0';
        }
        return `₹ ${params  .toLocaleString('en-IN')}`;
      }
    },
    { 
      field: 'last_transaction_date', 
      headerName: 'Last Sale', 
      width: 130,
      valueFormatter: (params) => {
        if (!params) {
          return 'Never';
        }
        try {
          return format(new Date(params), 'dd/MM/yyyy');
        } catch (error) {
          return 'Invalid date';
        }
      }
    }
  ];
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Dead Stock Analysis
        </Typography>
        <Button 
          onClick={onClose} 
          color="inherit" 
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Summary</Typography>
            <Typography variant="body1">
              <strong>Total Items:</strong> {data.total_count || 0} SKUs
            </Typography>
            <Typography variant="body1">
              <strong>Total Value:</strong> ₹ {data.total_value?.toLocaleString('en-IN') || 0}
            </Typography>
            <Typography variant="body1">
              <strong>No Sales For:</strong> {data.days_threshold || 90}+ days
            </Typography>
          </Box>
          
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" size="small">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: '250px' }}
          />
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ height: 'calc(100vh - 308px)', width: '100%' }}>
          <DataGrid
            rows={filteredItems}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            getRowId={(row) => row.variant_id}
            density="standard"
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" color="primary">
          Generate Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeadStockList;
